"use client";

import {
  formatMobileForDisplay,
  getSessionData,
  redirectWithUtmParam,
} from "@repo/shared-utils/common-utils";
import InvestButton from "./InvestButton";
import Conditional from "@repo/shared-ui/Conditional";
import BFLModal from "@repo/shared-ui/BFLModal";
import { useState, useEffect } from "react";
import {
  useAppSelector,
  selectIsAuthSuccessOrPerpetual,
  selectLeadStatus,
  selectLoginStatus,
  useAppDispatch,
  setLeadStatus,
  LeadStatus,
} from "@repo/global-store";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import { consentInsert, consentRead } from "@repo/sso";

import InvestNowConsentModal from "./InvestNowConsentModal";
import InvestNowOtpSendModal from "./InvestNowOtpSendModal";
import InvestNowOtpVerifyModal from "./InvestNowOtpVerifyModal";
import { CombinedClevertapProps, dataLayer } from "@repo/analytics/data-layer-utils";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { Heading } from "../../interfaces/IAMCContent.interface";

export const submitConsentInsert = async () => {
  const consentResponse: any = await consentInsert("BFSL", "BFL Entity", true);
  return consentResponse;
};

const InvestNowFlow = ({
  amcName,
  headingContent,
}: {
  amcName?: string;
  headingContent?: Heading;
}) => {
  const {
    redirectPTBlabel = "Resume Journey",
    redirectlabel = "Open MF Account",
    redirectETBlabe = "Invest Now",
    redirectlink = "",
    redirectApplink = "",
  } = headingContent ?? {};

  const isAuthSuccessOrPerpetual = useAppSelector(selectIsAuthSuccessOrPerpetual);
  const leadStatus = useAppSelector(selectLeadStatus);
  const loginStatus = useAppSelector(selectLoginStatus);
  const appDispatch = useAppDispatch();

  const [openModal, setOpenModal] = useState<"consent" | "send" | "verify" | "none">("none");
  const [consentProvided, setConsentProvided] = useState<boolean>(false);
  const [sessionMobileNo, setSessionMobileNo] = useState<string | undefined>(undefined);
  const [buttonText, setButtonText] = useState<string | undefined>(redirectlabel);

  const [showStickyCTA, setShowStickyCTA] = useState(false);

  /**
   * getConsentReadData
   */
  const getConsentReadData = async () => {
    const consentReadData = await consentRead("BFSL", "34", "BFL Entity");
    if (consentReadData?.data) {
      if (consentReadData?.data?.consentValue === "2") {
        setConsentProvided(true);
      }
    }
  };

  useEffect(() => {
    if (leadStatus === null) {
      console.warn("Lead status is null");
      return;
    }
    getConsentReadData();
    if (leadStatus === "Inprogress") {
      setButtonText(redirectPTBlabel);
    } else if (leadStatus === "ETB") {
      setButtonText(redirectETBlabe);
    } else {
      setButtonText(redirectlabel);
    }
  }, [leadStatus]);

  useEffect(() => {
    if (!isAuthSuccessOrPerpetual) {
      console.warn("Not fetching lead status as user is not logged in or perpetual");
      return;
    }

    const getLeadsData = async () => {
      const { leadsBuySell } = require("@repo/sso/leadsApi");
      const { mobileNo } = await getSessionData();

      if (mobileNo) {
        const leadsData = await leadsBuySell(mobileNo);

        if (leadsData?.statusCode === 90) {
          appDispatch(
            setLeadStatus({
              leadStatus: leadsData.data?.flag as LeadStatus,
            })
          );
        } else {
          appDispatch(
            setLeadStatus({
              leadStatus: "" as LeadStatus,
            })
          );
        }
      }
    };

    getLeadsData();
  }, [isAuthSuccessOrPerpetual]);

  // Get mobile number from session data
  useEffect(() => {
    let hasMounted = true;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const fetchSessionMobileNumber = async (rc = 0) => {
      try {
        const { mobileNo } = await getSessionData();
        if (mobileNo) {
          if (hasMounted) setSessionMobileNo(mobileNo);
        } else if (rc < 10 && hasMounted) {
          retryTimeoutId = setTimeout(() => fetchSessionMobileNumber(rc + 1), 500);
        }
      } catch (e) {
        if (rc < 10 && hasMounted) {
          retryTimeoutId = setTimeout(() => fetchSessionMobileNumber(rc + 1), 500);
        } else {
          console.warn("Error fetching session data:", e);
        }
      }
    };

    fetchSessionMobileNumber();

    return () => {
      hasMounted = false;
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
    };
  }, []);

  useEffect(() => {
    const heading = document.getElementById("Heading");
    if (!heading) return;

    const handleScroll = () => {
      setShowStickyCTA(heading.getBoundingClientRect().bottom < 0);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleOtpValidationSuccess = (otpValue: string) => {
    setOpenModal("none");
  };

  const handleOtpValidationFailure = (otpValue: string) => {
    // TODO - Keep modal open for retry
  };

  const handleCTAClick = async (label: string = "") => {
    const payload = {
      component: "amc_heading_bottom_cta",
      element: "cta_strip_card_button_click",
      event: "click",
      event_type: "button_click",
      destination_url: "",
      section_title: amcName,
      cta_text: label,
      product_name: "bajaj_mutual_fund",
      category_name: "loan",
      page_type: "pdp",
      customer_tag: loginStatus,
      is_loggedin: loginStatus,
      component_position: "",
      component_id: `{${amcName}/similar_funds`,
    };
    await dataLayer(loginStatus, payload, payload as unknown as CombinedClevertapProps);
  };

  return (
    <>
      <Conditional
        if={(isAuthSuccessOrPerpetual && leadStatus === null) || loginStatus === "pending"}
      >
        <BFLSkeleton width={225} count={1} height={40} />
      </Conditional>

      <Conditional
        if={
          (isAuthSuccessOrPerpetual &&
            (leadStatus === "" || leadStatus === "null" || leadStatus === "Inprogress")) ||
          loginStatus === "failure"
        }
      >
        <InvestButton
          ButtonText={buttonText}
          onClick={async () => {
            await handleCTAClick(buttonText);
            if (!isAuthSuccessOrPerpetual) {
              redirectWithUtmParam(isOpenInApp() ? redirectApplink : redirectlink, "false");
              return;
            }

            const modalToOpen = consentProvided ? "send" : "consent";
            setOpenModal(modalToOpen);
          }}
        />
      </Conditional>

      {/* Invest Now */}
      <Conditional if={isAuthSuccessOrPerpetual && leadStatus === "ETB"}>
        <InvestButton
          ButtonText={redirectlabel}
          onClick={async () => {
            await handleCTAClick(redirectlabel);
            redirectWithUtmParam(isOpenInApp() ? redirectApplink : redirectlink, "false");
          }}
        />
      </Conditional>

      {showStickyCTA && (
        <div className="fixed right-0 bottom-0 z-[6] p-3 md:hidden">
          <InvestButton
            ButtonText={
              isAuthSuccessOrPerpetual && leadStatus === "ETB" ? redirectlabel : buttonText
            }
            onClick={async () => {
              const label =
                isAuthSuccessOrPerpetual && leadStatus === "ETB" ? redirectlabel : buttonText;

              await handleCTAClick(label);

              if (leadStatus !== "Inprogress") {
                redirectWithUtmParam(isOpenInApp() ? redirectApplink : redirectlink, "false");
                return;
              }

              const modalToOpen = consentProvided ? "send" : "consent";
              setOpenModal(modalToOpen);
            }}
          />
        </div>
      )}
      <Conditional if={openModal === "consent"}>
        <BFLModal
          data-testid="bfl-modal"
          isOpen={true}
          onClose={() => {
            setOpenModal("none");
          }}
          title="Redirecting you to Bajaj Financial Securities Limited"
          secondaryButtonText="Skip"
          onSecondaryButtonClick={() => {
            setOpenModal("none");
            redirectWithUtmParam(isOpenInApp() ? redirectApplink : redirectlink, "false");
          }}
          onPrimaryButtonClick={async () => {
            try {
              const consentResponse = await submitConsentInsert();

              if (consentResponse.statusCode === 90 && consentResponse.status === "success") {
                setConsentProvided(true);
                if (consentResponse.data?.mobileNumber) {
                  setSessionMobileNo(consentResponse.data.mobileNumber);
                }
                setOpenModal("send");
              } else {
                console.error("Consent API failed:", consentResponse.message);
                setConsentProvided(false);
              }
            } catch (error) {
              console.error("Error in consent submission:", error);
              setConsentProvided(false);
            }
          }}
          enableScroll={false}
          buttonText="Continue"
        >
          <InvestNowConsentModal />
        </BFLModal>
      </Conditional>

      <Conditional if={openModal === "send"}>
        <BFLModal
          isOpen={true}
          onClose={() => {
            setOpenModal("none");
          }}
          onPrimaryButtonClick={() => {
            setOpenModal("verify");
          }}
          title="Open your free Demat and trading account"
          enableScroll={false}
          subtitle="Kickstart your investment journey today"
        >
          <InvestNowOtpSendModal
            setOpenModal={setOpenModal}
            prefillMobileNumber={sessionMobileNo}
          />
        </BFLModal>
      </Conditional>

      <Conditional if={openModal === "verify"}>
        <BFLModal
          isOpen={true}
          onClose={() => {
            setOpenModal("none");
          }}
          title="Verification required"
          subtitle={
            sessionMobileNo
              ? `A one-time password has been sent to your mobile number +91 ${formatMobileForDisplay(sessionMobileNo)}`
              : "A one-time password has been sent to your registered mobile number"
          }
        >
          <InvestNowOtpVerifyModal
            onValidationSuccess={handleOtpValidationSuccess}
            onValidationFailure={handleOtpValidationFailure}
            mobileNumber={sessionMobileNo}
          />
        </BFLModal>
      </Conditional>
    </>
  );
};

export default InvestNowFlow;
